import React, { useState } from 'react';
import { Badge, Card, CardContent } from '@lognai/synapse-design-system-v2';
import type { AiDraftItem } from '@lognai/plugin-ai-core/src/types/draft-item';
import type { AiRiskBand } from '@lognai/plugin-ai-core/src/types/bundle';
import { HighRiskConfirm } from './HighRiskConfirm';
import { computeDraftDiff } from './draft-diff';

const formatDiffValue = (v: unknown): string => {
  if (v == null) return '∅';
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

type BadgeTone = 'neutral' | 'success' | 'warning' | 'destructive' | 'info';

const RISK_TONE: Record<AiRiskBand, BadgeTone> = {
  low: 'info',
  medium: 'warning',
  high: 'destructive',
};

const RISK_LABEL: Record<AiRiskBand, string> = {
  low: 'LOW',
  medium: 'MEDIUM',
  high: 'HIGH',
};

export interface DraftRowProps {
  item: AiDraftItem;
  selected: boolean;
  onToggleSelect: () => void;
  highRiskConfirmed: boolean;
  onConfirmHighRisk: (confirmed: boolean) => void;
  /** When true, hides selection checkbox and high-risk confirm input. */
  readOnly?: boolean;
  /** If set, the row is shown disabled with this reason text (e.g. "Predecessor
   *  failed"). Empty / undefined means the row is selectable. */
  blockedBy?: string;
}

export const DraftRow: React.FC<DraftRowProps> = ({
  item,
  selected,
  onToggleSelect,
  highRiskConfirmed,
  onConfirmHighRisk,
  readOnly = false,
  blockedBy,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isHighRisk = item.riskBand === 'high';
  const isPendingOrApproved = item.status === 'pending' || item.status === 'approved';
  const isChained = (item.dependsOn?.length ?? 0) > 0;

  // High-risk items can only be selected after confirmation
  const canSelect = !isHighRisk || highRiskConfirmed;

  return (
    <Card style={{ marginBottom: 8 }}>
      <CardContent>
        {item.status === 'conflict' && (
          <div
            role="alert"
            data-testid="conflict-banner"
            style={{
              marginBottom: 8,
              padding: 8,
              borderRadius: 4,
              backgroundColor: 'var(--synapse-destructive-surface, #fef2f2)',
              border: '1px solid var(--synapse-destructive-border, #fecaca)',
              color: 'var(--synapse-destructive-text, #991b1b)',
              fontSize: 13,
            }}
          >
            <strong>Conflict:</strong>{' '}
            {item.preconditionCheckResult?.reason ??
              'Underlying entity changed since this draft was proposed.'}{' '}
            Reject this draft and re-ask the AI to redraft with current state.
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          {!readOnly && (
            <input
              type="checkbox"
              data-testid={item.draftIndex != null ? `select-draft-${item.draftIndex}` : undefined}
              checked={selected}
              disabled={!canSelect || !isPendingOrApproved || Boolean(blockedBy)}
              onChange={onToggleSelect}
              aria-label={`Select ${item.uiLabel}`}
              style={{ marginTop: 4 }}
            />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Badge tone={RISK_TONE[item.riskBand]}>{RISK_LABEL[item.riskBand]}</Badge>
              {isChained && (
                <span
                  data-testid={item.draftIndex != null ? `chain-icon-draft-${item.draftIndex}` : 'chain-icon'}
                  aria-label="part of a chain"
                  title={`Depends on draft #${(item.dependsOn ?? []).join(', #')}`}
                  style={{ fontSize: 12 }}
                >
                  🔗
                </span>
              )}
              <span style={{ fontWeight: 500, fontSize: 14 }}>{item.uiLabel}</span>
            </div>
            {blockedBy && (
              <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>{blockedBy}</div>
            )}
            <div style={{ fontSize: 13, color: 'var(--synapse-text-muted, #6b7280)', marginTop: 4 }}>
              {item.uiSummary}
            </div>
            {item.draftBaseline && (
              <div data-testid="draft-diff" style={{ marginTop: 6, fontSize: 12 }}>
                {computeDraftDiff(item).map((row) => (
                  <div
                    key={row.key}
                    style={{ display: 'flex', alignItems: 'baseline', gap: 6, lineHeight: 1.6 }}
                  >
                    <span style={{ color: 'var(--synapse-text-muted, #6b7280)' }}>{row.key}:</span>
                    <span style={{ textDecoration: 'line-through', color: 'var(--synapse-text-muted, #9ca3af)' }}>
                      {formatDiffValue(row.from)}
                    </span>
                    <span style={{ color: 'var(--synapse-text-muted, #6b7280)' }}>→</span>
                    {row.isProposed ? (
                      <span style={{ fontWeight: 500 }}>{formatDiffValue(row.to)}</span>
                    ) : (
                      <em style={{ color: 'var(--synapse-text-muted, #9ca3af)' }}>(unchanged)</em>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: 12, color: 'var(--synapse-text-muted, #9ca3af)', marginTop: 4 }}>
              ▸ {item.reversible ? 'Reversible · 30s undo' : 'Not reversible'}
              {item.status !== 'pending' && item.status !== 'approved' ? ` · ${item.status}` : ''}
            </div>
            {!readOnly && isHighRisk && isPendingOrApproved && (
              <div style={{ marginTop: 8 }}>
                <HighRiskConfirm
                  expectedId={item.entityId ?? item.id}
                  confirmed={highRiskConfirmed}
                  onConfirm={onConfirmHighRisk}
                />
              </div>
            )}
            <button
              type="button"
              onClick={() => setExpanded((p) => !p)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--synapse-text-muted, #6b7280)',
                fontSize: 12,
                cursor: 'pointer',
                marginTop: 8,
                padding: 0,
              }}
            >
              {expanded ? '▾ Hide details' : '▸ Show details'}
            </button>
            {expanded && (
              <pre style={{
                marginTop: 8,
                padding: 8,
                backgroundColor: 'var(--synapse-surface-muted, #f9fafb)',
                fontSize: 11,
                borderRadius: 4,
                overflow: 'auto',
                maxHeight: 200,
              }}>
                {JSON.stringify(item.payload, null, 2)}
              </pre>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
